import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, CloudSun, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OVERLAYS = ['radar', 'rain', 'wind', 'gust', 'waves', 'temp', 'clouds'];
const TEMP_UNITS = [
  { value: 'fahrenheit', label: 'Fahrenheit (°F)' },
  { value: 'celsius', label: 'Celsius (°C)' },
];
const WIND_UNITS = [
  { value: 'mph', label: 'mph' },
  { value: 'kn', label: 'knots' },
  { value: 'kmh', label: 'km/h' },
  { value: 'ms', label: 'm/s' },
];
const PRECIP_UNITS = [
  { value: 'inch', label: 'inches' },
  { value: 'mm', label: 'millimetres' },
];

const AdminWeather = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/weather/page`, { headers: authHeaders() });
        setPage(data || {});
      } catch (error) {
        toast({
          title: 'Load failed',
          description: error.response?.data?.detail || 'Could not load weather settings.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...page,
        latitude: Number(page.latitude),
        longitude: Number(page.longitude),
        radar_zoom: Number(page.radar_zoom) || 8,
      };
      const { data } = await axios.put(`${API}/weather/page`, payload, { headers: authHeaders() });
      setPage(data || {});
      toast({ title: 'Saved', description: 'Weather settings updated. The forecast cache was cleared.' });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.response?.data?.detail || 'Could not save weather settings.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !page) {
    return (
      <div className="flex justify-center py-24" data-testid="weather-admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const set = (key) => (e) => setPage({ ...page, [key]: e.target.value });

  return (
    <div className="max-w-3xl" data-testid="admin-weather">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CloudSun className="w-7 h-7 text-blue-600" />
            Island Weather
          </h2>
          <p className="text-gray-500 mt-1">
            Location, units and radar defaults for the public weather page. Turn the page on or off
            with the <strong>weather_enabled</strong> feature flag.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="weather-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Location</CardTitle>
          <CardDescription>Drives the live forecast and the centre of the radar map.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Location Name</Label>
              <Input value={page.location_name || ''} onChange={set('location_name')} className="mt-1"
                data-testid="weather-location-name" />
            </div>
            <div>
              <Label>Region Line</Label>
              <Input value={page.region_name || ''} onChange={set('region_name')} className="mt-1"
                data-testid="weather-region-name" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Latitude</Label>
              <Input type="number" step="0.0001" value={page.latitude ?? ''} onChange={set('latitude')}
                className="mt-1" data-testid="weather-lat" />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input type="number" step="0.0001" value={page.longitude ?? ''} onChange={set('longitude')}
                className="mt-1" data-testid="weather-lon" />
            </div>
            <div>
              <Label>Timezone</Label>
              <Input value={page.timezone || ''} onChange={set('timezone')} className="mt-1"
                data-testid="weather-tz" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Units</CardTitle>
          <CardDescription>Applied to the forecast, hourly strip and stat tiles.</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Temperature</Label>
            <select value={page.temperature_unit || 'fahrenheit'} onChange={set('temperature_unit')}
              className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              data-testid="weather-temp-unit">
              {TEMP_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Wind Speed</Label>
            <select value={page.wind_speed_unit || 'mph'} onChange={set('wind_speed_unit')}
              className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              data-testid="weather-wind-unit">
              {WIND_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Precipitation</Label>
            <select value={page.precipitation_unit || 'inch'} onChange={set('precipitation_unit')}
              className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              data-testid="weather-precip-unit">
              {PRECIP_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Radar Map</CardTitle>
          <CardDescription>Default layer and zoom for the interactive map.</CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Default Layer</Label>
            <select value={page.radar_default_overlay || 'radar'} onChange={set('radar_default_overlay')}
              className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              data-testid="weather-overlay">
              {OVERLAYS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <Label>Zoom (4 wide &ndash; 12 close)</Label>
            <Input type="number" min="3" max="14" value={page.radar_zoom ?? 8} onChange={set('radar_zoom')}
              className="mt-1" data-testid="weather-zoom" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Intro Copy (optional)</CardTitle>
          <CardDescription>Shown under the page heading if set.</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea value={page.intro_body || ''} onChange={set('intro_body')} rows={3}
            data-testid="weather-intro" />
          <p className="text-xs text-gray-500 mt-3 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            Forecast data from Open-Meteo (no API key). Free for non-commercial use &mdash; a paid plan
            may be required for commercial traffic.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminWeather;
